import { OptimisticUpdate, optimisticBlockStore } from "../../block/optimisticBlockStore";
import { BlockType } from "../../../types/block/type";
import { isTimestampNewer } from "./utils";
import { ProcessingContext } from "./type";

export const processDeletedBlock = (): BlockType | null => {
  // NOTE 이 함수는 서버 블록이 있는 경우만 처리되는 로직이기 떄문에 낙관적으로 삭제된 경우에만 동작
  // 낙관적 삭제 시에는 서버 응답이 삭제되어 올 때까지 삭제된 채로 보이기만 하면 됨. 실제 서버에서 삭제되면 배열에서 제거 가능
  return null;
};

export const processNewBlock = (
  newBlockPatch: OptimisticUpdate,
  serverData: BlockType[]
): BlockType => {
  const patchData = newBlockPatch.data;
  const relatedServerBlock = serverData.find((block) => block.clientId === patchData?.clientId);

  // NOTE isNewBlock인데 서버에 관련 블록이 없는 경우
  if (!relatedServerBlock) {
    return patchData;
  }

  const isPatchNewer = isTimestampNewer(patchData.updatedAt, relatedServerBlock.updatedAt);

  // NOTE 서버에 있는 블록이 더 최신인 경우 낙관적 업데이트 배열에서 삭제
  if (!isPatchNewer) {
    optimisticBlockStore.remove({ clientId: String(patchData.clientId) });
    return relatedServerBlock;
  }

  return patchData;
};

export const processUpdatedBlock = (serverItem: BlockType, patchData: BlockType): BlockType => {
  const isPatchNewer = isTimestampNewer(patchData.updatedAt, serverItem.updatedAt);

  // NOTE 서버에 있는 블록이 더 최신인 경우 낙관적 업데이트 배열에서 삭제
  if (!isPatchNewer) {
    optimisticBlockStore.remove({ id: String(serverItem.id) });
    return serverItem;
  }
  return patchData;
};

export const processServerBlocks = (
  serverItem: BlockType,
  context: ProcessingContext,
  serverData: BlockType[]
): BlockType | null => {
  const { optimisticMaps } = context;
  // NOTE deleted 블록 처리
  if (optimisticMaps.deletedPatchMap.has(String(serverItem.id))) {
    return processDeletedBlock();
  }
  // NOTE isNewBlock 처리: (서버에 있는 블록과 clientId가 같은 경우)
  if (serverItem.clientId && optimisticMaps.newBlockPatchMap.has(serverItem.clientId)) {
    const newBlockPatch = optimisticMaps.newBlockPatchMap.get(serverItem.clientId)!;
    return processNewBlock(newBlockPatch, serverData);
  }
  // NOTE 일반 업데이트된 블록 처리
  if (optimisticMaps.updatePatchMap.has(String(serverItem.id))) {
    const patchData = optimisticMaps.updatePatchMap.get(String(serverItem.id))!;
    return processUpdatedBlock(serverItem, patchData);
  }
  // NOTE 변경사항이 없는 블록
  return serverItem;
};

export const processBlocksNotInServer = (
  patches: OptimisticUpdate[],
  context: ProcessingContext
): BlockType[] => {
  return patches
    .filter((patch) => {
      // 새로운 블록 중 서버에 없는 것들만
      if (patch.isNewBlock) {
        return !context.serverClientIds.has(patch.data?.clientId ?? "");
      }
      // NOTE 삭제된 블록은 여기서 제외하고 보여줌.
      if (patch.isDeletedBlock) {
        return false;
      }
      // 서버에 없는 일반 업데이트들
      return !context.serverIds.has(String(patch.id));
    })
    .map((patch) => patch.data);
};
